/**
 * Data Explorer route - thin orchestrator for one browse view (sales /
 * products / clients) on the shared exception-table stack.
 *
 * Deliberately a sibling of `DynamicExceptionRoute` without the
 * exception-only machinery (type fetch, status toggle, oldest-open
 * chip, summary chips, filter strips, sibling strip, legacy redirects).
 * URL state, filter values, panel, table and drawer are the same
 * shared pieces, so a record renders identically here and on
 * exception pages.
 *
 * Drawer deep-link contract: three URL params -
 * `?exceptionObject=<full tag object_id>` + `?drawerCode=` +
 * `?drawerDomain=` - written from the row's backend-stamped
 * `primary_*` tuple. Products may carry a `product_id:outlet_id`
 * object_id, which is why the tuple rides explicit fields rather than
 * the row's own id.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useInspectorRef } from '../../../hooks/useInspectorRef'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import { Helmet } from 'react-helmet-async'

import { DataViewTable } from '../../../components/shared/DataViewTable'
import DataViewFilterPanel from '../../../components/inspector/shared/DataViewFilterPanel'
import ExceptionDrawer from '../../../components/inspector/shared/ExceptionDrawer'
import { buildActiveFilterChips } from '../../../components/inspector/shared/buildActiveFilterChips'
import { useInspector } from '../../../contexts/InspectorContext'
import { useExceptionUrlState } from '../../../hooks/useExceptionUrlState'
import { useExceptionFilterValues } from '../../../hooks/useExceptionFilterValues'
import { useExceptionDrawerDetail } from '../../../hooks/useExceptionDrawerDetail'
import { useDataExplorerFetch } from '../../../hooks/useDataExplorerFetch'
import {
  getDataExplorerViewConfig,
  toApiSortKey,
  type DataViewFacets,
  type DataViewKey,
} from '../../../config/dataExplorer'

interface DrawerTuple {
  object_id: string
  /** Absent for an object-only anchor: a zero-issue browse row still opens
   *  the drawer in object-history mode (lifecycle + comments) without a
   *  focused exception code. */
  exception_code?: string
  data_domain: string
}

/** Pick the drawer tuple for a clicked row: backend-stamped primary
 * first, then the first actionable open entry, then the latest
 * recently-closed entry. */
const tupleForRow = (row: Record<string, any>): DrawerTuple | null => {
  if (row.primary_exception_code && row.primary_data_domain) {
    return {
      object_id: String(row.primary_object_id ?? row.object_id ?? ''),
      exception_code: row.primary_exception_code,
      data_domain: row.primary_data_domain,
    }
  }
  const entries = (row.errors ?? []) as Array<Record<string, any>>
  const actionable = entries.find((e) => !e.is_umbrella) ?? entries[0]
  const closed = ((row.recently_closed_errors ?? []) as Array<Record<string, any>>)[0]
  const entry = actionable ?? closed
  if (!entry) {
    // No open or recently-closed exception, but the backend stamps a stable
    // (object_id, data_domain) on every browse row. Anchor the drawer on that
    // so a zero-issue object still opens its history / comments - just without
    // a focused exception code.
    if (row.object_id != null && row.data_domain) {
      return {
        object_id: String(row.object_id),
        data_domain: String(row.data_domain),
      }
    }
    return null
  }
  return {
    object_id: String(entry.object_id ?? row.object_id ?? ''),
    exception_code: entry.code,
    data_domain: entry.data_domain,
  }
}

const DataExplorerRoute: React.FC<{ viewKey: DataViewKey }> = ({ viewKey }) => {
  const { fpCode: fp_code } = useInspectorRef()
  const [searchParams, setSearchParams] = useSearchParams()
  const { franchiseData } = useInspector()
  const config = getDataExplorerViewConfig(viewKey)

  // ── URL state (paging + debounced search + drawer object) ─────────
  const {
    urlState,
    searchInput,
    setSearchInput,
    setPage,
    setPageSize,
    searchEnabled,
    exceptionObject,
    setExceptionObject,
  } = useExceptionUrlState({ config, searchParams, setSearchParams })

  // Filter values keyed off the STATIC config (resolveFilters only
  // changes options, never keys/defaults - using the resolved config
  // here would reset values every facet refresh).
  const {
    filterValues,
    updateFilterValue,
    filterEnabled,
    setFilterEnabled,
    resetFilters,
    hasAnyActiveFilter,
  } = useExceptionFilterValues(config)

  // ── Server sort in the URL (?sortBy / ?sortDir, column-key shaped) ─
  const sortKeys = config.serverSort.sortableKeys
  const rawSortBy = searchParams.get('sortBy') ?? ''
  const sortBy = sortKeys.includes(rawSortBy)
    ? rawSortBy
    : config.serverSort.defaultSortBy
  const rawSortDir = searchParams.get('sortDir')
  const sortDir: 'asc' | 'desc' =
    rawSortDir === 'asc' || rawSortDir === 'desc'
      ? rawSortDir
      : config.serverSort.defaultSortDir
  const handleSortChange = (key: string) => {
    const next = new URLSearchParams(searchParams)
    const nextDir =
      key === sortBy ? (sortDir === 'asc' ? 'desc' : 'asc') : 'desc'
    if (key === config.serverSort.defaultSortBy) next.delete('sortBy')
    else next.set('sortBy', key)
    if (nextDir === config.serverSort.defaultSortDir) next.delete('sortDir')
    else next.set('sortDir', nextDir)
    next.delete('page')
    setSearchParams(next, { replace: true })
  }

  // ── Data ───────────────────────────────────────────────────────────
  const { data, response, loading, error: fetchError, refetch } =
    useDataExplorerFetch({
      fpCode: fp_code,
      config,
      urlState,
      filterValues,
      sortBy: toApiSortKey(sortBy),
      sortDir,
    })

  // ── FP-derived filter options (facets endpoint + response outlets) ─
  const [endpointFacets, setEndpointFacets] = useState<DataViewFacets | null>(null)
  useEffect(() => {
    setEndpointFacets(null)
    if (!fp_code || !config.fetchFacets) return
    let cancelled = false
    config
      .fetchFacets(fp_code)
      .then((res) => {
        if (!cancelled) setEndpointFacets(res)
      })
      .catch((err: unknown) => {
        console.error('Failed to fetch data explorer facets:', err)
      })
    return () => {
      cancelled = true
    }
  }, [fp_code, config])
  // Outlets ride the list response; keep the last non-empty list so
  // filter options don't flicker away while a page loads.
  const [responseOutlets, setResponseOutlets] = useState<
    Array<{ id: string; name: string }> | undefined
  >(undefined)
  useEffect(() => {
    if (response?.outlets?.length) setResponseOutlets(response.outlets)
  }, [response])
  const facets = useMemo<DataViewFacets | null>(() => {
    if (!endpointFacets && !responseOutlets) return null
    return { ...(endpointFacets ?? {}), outlets: responseOutlets }
  }, [endpointFacets, responseOutlets])

  const resolvedConfig = useMemo(
    () =>
      config.resolveFilters
        ? { ...config, filters: config.resolveFilters(facets) }
        : config,
    [config, facets],
  )

  // ── Drawer tuple (three URL params) ────────────────────────────────
  const drawerCode = searchParams.get('drawerCode')
  const drawerDomain = searchParams.get('drawerDomain')
  const drawerTuple = useMemo(
    () =>
      exceptionObject && drawerCode && drawerDomain
        ? {
            data_domain: drawerDomain,
            exception_code: drawerCode,
            object_id: exceptionObject,
          }
        : null,
    [exceptionObject, drawerCode, drawerDomain],
  )
  const writeDrawerTuple = (tuple: DrawerTuple | null) => {
    const next = new URLSearchParams(searchParams)
    if (tuple) {
      next.set('exceptionObject', tuple.object_id)
      next.set('drawerDomain', tuple.data_domain)
      // Object-only anchor (no current exception) omits the code param so the
      // URL stays clean and the central detail fetch stays disabled - the
      // on-page row seeds the drawer instead.
      if (tuple.exception_code) next.set('drawerCode', tuple.exception_code)
      else next.delete('drawerCode')
    } else {
      next.delete('exceptionObject')
      next.delete('drawerCode')
      next.delete('drawerDomain')
    }
    setSearchParams(next, { replace: true })
  }
  // On-page rows seed the drawer instantly; the central detail endpoint
  // re-resolves for full sibling context + name/allow-ignore metadata
  // (and is the only source for off-page deep links).
  const localRow = useMemo(() => {
    if (!exceptionObject) return null
    return (
      data.find(
        (r) =>
          String(r.primary_object_id ?? '') === exceptionObject ||
          String(r.object_id ?? '') === exceptionObject,
      ) ?? null
    )
  }, [data, exceptionObject])
  const drawerDetail = useExceptionDrawerDetail(fp_code, drawerTuple, !!drawerTuple)
  const drawerRow = drawerDetail.row ?? localRow
  const [unresolvableAlert, setUnresolvableAlert] = useState<string | null>(null)
  useEffect(() => {
    if (!exceptionObject || localRow) return
    // Coded anchor: the central detail fetch errored (object aged out /
    // resolved). Object-only anchor (no drawerCode): that fetch is disabled,
    // so there is no error to key off - the row can only come from the on-page
    // data. Once the list has settled and the object still isn't present, it's
    // unresolvable the same way, so close + alert instead of leaving an empty
    // drawer open.
    const codedUnresolvable = !!drawerTuple && !!drawerDetail.error
    const objectOnlyUnresolvable = !drawerTuple && !loading
    if (!codedUnresolvable && !objectOnlyUnresolvable) return
    setUnresolvableAlert(
      `That linked row (object ${exceptionObject}) is no longer available - it may have been resolved or the data has aged out.`,
    )
    writeDrawerTuple(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerDetail.error, exceptionObject, localRow, drawerTuple, loading])

  // ── Toolbar chips + reset ──────────────────────────────────────────
  const activeFilterChips = buildActiveFilterChips({
    strips: [],
    config: resolvedConfig,
    response,
    rows: data,
    filterCounts: null,
    searchParams,
    setSearchParams,
    filterValues,
    updateFilterValue,
    filterEnabled,
    setFilterEnabled,
    // No summary-chip axis on browse views; the builder requires the slots.
    activeChipKey: null,
    setActiveChipKey: () => {},
    serverSearch: searchEnabled
      ? { value: searchInput, onChange: setSearchInput }
      : undefined,
  })
  const handleResetFilters = () => {
    resetFilters()
    if (searchEnabled) setSearchInput('')
    setSearchParams(new URLSearchParams(), { replace: false })
  }
  const hasAnyActiveSelection =
    hasAnyActiveFilter || (searchEnabled && !!searchInput) || activeFilterChips.length > 0

  const franchiseName = franchiseData?.franchise_name || fp_code || 'Loading'
  const pageTitle = `${config.pageHeader.breadcrumb} | ${franchiseName} | Inspector Tool`

  const emptyStateNode = (
    <Stack alignItems="center" spacing={1.25} sx={{ py: 8, px: 3, width: '100%', textAlign: 'center' }}>
      <SearchOffIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
      <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}>
        No records found
      </Typography>
      <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
        Try clearing the filters or widening the date range.
      </Typography>
    </Stack>
  )

  return (
    <Box>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
          {config.pageHeader.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {config.pageHeader.description}
        </Typography>
      </Box>
      <DataViewFilterPanel
        config={resolvedConfig}
        strips={[]}
        response={response}
        rows={data}
        filterCounts={null}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        filterValues={filterValues}
        updateFilterValue={updateFilterValue}
        filterEnabled={filterEnabled}
        setFilterEnabled={setFilterEnabled}
        onReset={handleResetFilters}
        hasAnyActiveFilter={hasAnyActiveSelection}
        serverSearch={
          searchEnabled
            ? { value: searchInput, onChange: setSearchInput }
            : undefined
        }
      />
      {unresolvableAlert && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setUnresolvableAlert(null)}>
          {unresolvableAlert}
        </Alert>
      )}
      {fetchError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {fetchError}
        </Alert>
      ) : (
        <DataViewTable
          config={resolvedConfig}
          data={data}
          response={response || undefined}
          variant="inspector"
          fpCode={fp_code!}
          isLoading={loading}
          emptyMessage={emptyStateNode}
          title={config.pageHeader.title}
          serverPagination={
            typeof response?.total === 'number'
              ? {
                  page: urlState.page,
                  pageSize: urlState.pageSize,
                  total: response.total,
                  onPageChange: setPage,
                  onPageSizeChange: setPageSize,
                  rowsPerPageOptions: config.serverTable?.pageSizeOptions ?? [50],
                }
              : undefined
          }
          serverSearch={
            searchEnabled
              ? { value: searchInput, onChange: setSearchInput }
              : undefined
          }
          serverSort={{
            orderBy: sortBy,
            order: sortDir,
            sortableKeys: sortKeys,
            onSortChange: handleSortChange,
          }}
          filterValues={filterValues}
          updateFilterValue={updateFilterValue}
          filterEnabled={filterEnabled}
          activeFilterChips={activeFilterChips}
          onAfterMutation={refetch}
          controlledExceptionObject={exceptionObject}
          onRowOpen={(row) => {
            if (!row) {
              writeDrawerTuple(null)
              return
            }
            writeDrawerTuple(tupleForRow(row as Record<string, any>))
          }}
        />
      )}
      {fp_code && (exceptionObject || drawerRow) && (
        <ExceptionDrawer
          open={!!exceptionObject}
          loading={localRow === null ? drawerDetail.loading : false}
          onClose={() => writeDrawerTuple(null)}
          row={drawerRow}
          fpCode={fp_code}
          exceptionTypeName={drawerDetail.nameShort ?? undefined}
          exceptionType={drawerDetail.exceptionType ?? undefined}
          allowIgnore={drawerDetail.allowIgnore}
          getTitle={config.getRowTitle}
          getRowTitle={config.getRowTitle}
          getRowSubtitle={config.getRowSubtitle}
          franchiseData={franchiseData}
          onAfterMutation={refetch}
        />
      )}
    </Box>
  )
}

export default DataExplorerRoute
